import React from 'react';
import Logo from './Logo';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <Logo size="default" variant="glow" />
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600 italic mb-8">Effective Date: December 8, 2025</p>

          <div className="prose prose-lg max-w-none space-y-6 text-gray-800">
            <p>
              Welcome to Stomp. These Terms of Service ("Terms") govern your access to and use of the Stomp application, 
              website, and services (collectively, the "Service"). By accessing or using the Service, you agree to be bound 
              by these Terms. If you do not agree to these Terms, you may not use the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Description of Service</h2>
            <p>
              Stomp is a streaming service advisor application that helps users discover streaming content, identify subscription 
              perks available through credit cards and other memberships, and optimize their streaming subscriptions. The Service 
              provides recommendations and information based on data you provide and publicly available information.
            </p>
            <p>
              Stomp does not provide streaming content directly. We are an advisory and aggregation service that helps you make 
              informed decisions about your streaming subscriptions.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Account Registration</h2>
            <p>
              To use certain features of the Service, you must create an account. You agree to provide accurate, current, and 
              complete information during registration and to update such information to keep it accurate, current, and complete. 
              You are responsible for safeguarding your account credentials and for all activities that occur under your account.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Subscription Plans and Payment</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.1 Free and Premium Tiers</h3>
            <p>
              The Service offers both Free and Premium subscription tiers. The Free tier provides limited access to features, 
              while the Premium tier provides full access to all features. Feature availability may change at our discretion.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.2 Billing</h3>
            <p>
              Premium subscriptions are billed in advance on a recurring basis (monthly or annually, depending on your selection). 
              Payment is processed through Stripe, our third-party payment processor. By subscribing, you authorize us to charge 
              your payment method for the subscription fees.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.3 Cancellation and Refunds</h3>
            <p>
              You may cancel your Premium subscription at any time through your account settings. Cancellation will take effect 
              at the end of your current billing period. We do not provide refunds for partial subscription periods, except as 
              required by applicable law.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.4 Price Changes</h3>
            <p>
              We reserve the right to change subscription prices. Any price changes will be communicated to you in advance and 
              will take effect at the start of your next billing period.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Acceptable Use</h2>
            <p>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>use the Service in any way that violates any applicable law or regulation;</li>
              <li>attempt to gain unauthorized access to any part of the Service;</li>
              <li>use any automated means to access the Service or collect data from it;</li>
              <li>interfere with or disrupt the Service or servers or networks connected to the Service; or</li>
              <li>impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by Stomp and are protected by 
              international copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks 
              may not be used in connection with any product or service without our prior written consent.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Third-Party Services</h2>
            <p>
              The Service may contain links to or integrate with third-party websites, services, or content. We do not control 
              and are not responsible for the content, privacy policies, or practices of any third-party services. Your use of 
              third-party services is at your own risk and subject to the terms and conditions of those services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Disclaimer of Warranties</h2>
            <p className="font-semibold">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND 
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT ANY 
              DEFECTS WILL BE CORRECTED.
            </p>
            <p>
              Stomp provides recommendations and information for informational purposes only. We do not guarantee the accuracy, 
              completeness, or timeliness of any information provided. You are solely responsible for verifying any information 
              before making decisions based on it.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Limitation of Liability</h2>
            <p className="font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, STOMP AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE 
              FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER 
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM: 
              (A) YOUR USE OF OR INABILITY TO USE THE SERVICE; (B) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SERVERS AND/OR ANY 
              PERSONAL INFORMATION STORED THEREIN; (C) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SERVICE; OR 
              (D) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE THAT MAY BE TRANSMITTED TO OR THROUGH THE SERVICE BY ANY THIRD PARTY.
            </p>
            <p className="font-semibold">
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU HAVE PAID US IN THE TWELVE (12) 
              MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Stomp and its officers, directors, employees, agents, and affiliates 
              from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses arising from: 
              (a) your use of the Service; (b) your violation of these Terms; or (c) your violation of any third-party rights, 
              including intellectual property rights.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by email or 
              through the Service prior to the changes taking effect. Your continued use of the Service after the effective date 
              of the revised Terms constitutes your acceptance of the changes.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, 
              for any reason, including if you breach these Terms. Upon termination, your right to use the Service will immediately 
              cease. All provisions of these Terms which by their nature should survive termination shall survive.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Colorado, without regard 
              to its conflict of law provisions. Any dispute arising out of or relating to these Terms or the Service shall be 
              resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except 
              that either party may seek injunctive relief in any court of competent jurisdiction.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Miscellaneous</h2>
            <p>
              These Terms constitute the entire agreement between you and us regarding the Service and supersede all prior agreements. 
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any 
              provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in effect. You 
              may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations 
              under these Terms without restriction.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at{' '}
              <a href="mailto:support@stomp.app" className="text-purple-600 hover:text-purple-700 underline">
                support@stomp.app
              </a>
              .
            </p>
          </div>

          {/* Back Button */}
          <div className="mt-12 text-center">
            <button
              onClick={() => window.history.back()}
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Back to App
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;